(() => {
  'use strict';

  if (window.BaringProductFeaturesInitialized) return;
  window.BaringProductFeaturesInitialized = true;

  const RECENTLY_VIEWED_KEY = 'baring:recent-products:v1';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let zoomOpener = null;

  const parseJSON = (node, fallback = null) => {
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return fallback;
    }
  };

  const setText = (node, value) => {
    if (node) node.textContent = value;
  };

  const updateStock = (root, variant) => {
    const status = root.querySelector('[data-product-stock]');
    if (!status) return;

    const threshold = Number(root.dataset.lowStockThreshold || 5);
    const presale = root.dataset.presale === 'true';
    let state = 'in-stock';
    let message = 'In stock';

    if (!variant || !variant.available) {
      state = 'sold-out';
      message = variant ? 'Sold out' : 'Unavailable';
    } else if (presale) {
      state = 'presale';
      message = 'Available for presale';
    } else if (
      variant.inventoryManagement &&
      variant.inventoryPolicy === 'deny' &&
      Number(variant.inventoryQuantity) > 0 &&
      Number(variant.inventoryQuantity) <= threshold
    ) {
      state = 'low-stock';
      message = 'Low stock';
    }

    status.dataset.stockState = state;
    status.className = `product-stock product-stock--${state}`;
    status.textContent = message;
  };

  const updateBadge = (root, variant) => {
    const wrapper = root.querySelector('[data-product-badges]');
    const badge = wrapper?.querySelector('[data-product-badge]');
    if (!wrapper || !badge) return;

    const customBadges = [...wrapper.querySelectorAll('[data-product-custom-badge]')];
    const presale = root.dataset.presale === 'true';
    const onSale = Number(variant?.compareAtPriceAmount || 0) > Number(variant?.priceAmount || 0);
    let state = '';
    let label = '';

    if (!variant || !variant.available) {
      state = 'sold-out';
      label = 'Sold out';
    } else if (presale) {
      state = 'presale';
      label = 'Presale';
    } else if (onSale) {
      state = 'sale';
      label = 'Sale';
    }

    badge.hidden = !state;
    badge.className = `product-badge${state ? ` product-badge--${state}` : ''}`;
    setText(badge, label);
    customBadges.forEach((customBadge) => { customBadge.hidden = Boolean(state); });
    wrapper.hidden = !state && customBadges.length === 0;
  };

  const updateOptionAvailability = (picker, variants, selectedOptions) => {
    picker.querySelectorAll('[data-option-input]').forEach((input) => {
      const position = Number(input.dataset.optionPosition) - 1;
      const candidateOptions = selectedOptions.slice();
      candidateOptions[position] = input.value;
      const available = variants.some((variant) => (
        variant.available &&
        variant.options.every((value, index) => value === candidateOptions[index])
      ));
      const label = picker.querySelector(`label[for="${input.id}"]`);
      const availability = label?.querySelector('[data-option-availability]');

      if (label) label.toggleAttribute('data-option-unavailable', !available);
      input.disabled = !available;
      input.removeAttribute('aria-disabled');
      setText(availability, available ? '' : 'Unavailable');
    });
  };

  const initVariantPicker = (root) => {
    if (root.dataset.variantPickerReady === 'true') return;

    const picker = root.querySelector('[data-product-options]');
    const variantSelect = picker?.querySelector('[data-variant-select]');
    const variantData = parseJSON(picker?.querySelector('[data-product-variants]'), []);
    if (!picker || !variantSelect || !Array.isArray(variantData) || !variantData.length) return;

    root.dataset.variantPickerReady = 'true';
    const optionGroups = picker.querySelector('[data-option-groups]');

    if (optionGroups) {
      optionGroups.hidden = false;
      variantSelect.hidden = true;
      picker.querySelector('.product-options__fallback-label')?.setAttribute('hidden', '');
    }

    const selectedOptions = () => [...picker.querySelectorAll('[data-option-position]')]
      .filter((group) => group.matches('fieldset'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((group) => group.querySelector('[data-option-input]:checked')?.value || '');

    const render = ({ updateHistory = true } = {}) => {
      const options = optionGroups ? selectedOptions() : [];
      const variant = optionGroups
        ? variantData.find((item) => item.options.every((value, index) => value === options[index]))
        : variantData.find((item) => String(item.id) === variantSelect.value);

      picker.querySelectorAll('[data-option-position]').forEach((group) => {
        if (!group.matches('fieldset')) return;
        const checked = group.querySelector('[data-option-input]:checked');
        setText(group.querySelector('[data-selected-option]'), checked?.value || '');
      });

      if (optionGroups) updateOptionAvailability(picker, variantData, options);

      const addButton = root.querySelector('[data-add-to-bag]');
      const mobileAddButton = root.querySelector('[data-mobile-add-to-bag]');
      const price = root.querySelector('[data-product-price]');
      const mobilePrice = root.querySelector('[data-mobile-price]');
      const comparePrice = root.querySelector('[data-product-compare-price]');
      const available = Boolean(variant?.available);

      if (variant) variantSelect.value = String(variant.id);
      setText(price, variant?.price || 'Unavailable');
      setText(mobilePrice, variant?.price || 'Unavailable');

      if (comparePrice) {
        comparePrice.hidden = !variant?.compareAtPrice;
        setText(comparePrice, variant?.compareAtPrice || '');
      }

      [addButton, mobileAddButton].filter(Boolean).forEach((button) => {
        button.disabled = !available;
        button.textContent = available
          ? (button.dataset.availableText || 'Add to bag')
          : (button.dataset.unavailableText || 'Currently unavailable');
      });

      updateStock(root, variant);
      updateBadge(root, variant);

      root.querySelectorAll('[data-product-media-link]').forEach((link) => {
        const current = Boolean(variant?.featuredMediaId) && String(link.dataset.mediaId) === String(variant.featuredMediaId);
        if (current) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });

      if (variant && updateHistory && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      root.dispatchEvent(new CustomEvent('baring:variant-change', {
        bubbles: true,
        detail: { variant: variant || null }
      }));
    };

    picker.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-input], [data-variant-select]')) render();
    });
    render({ updateHistory: false });
  };

  const safeProductURL = (value) => {
    if (typeof value !== 'string') return null;
    try {
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return null;
      return `${url.pathname}${url.search}`;
    } catch (error) {
      return null;
    }
  };

  const safeImageURL = (value) => {
    if (typeof value !== 'string' || !value) return null;
    try {
      const url = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
    } catch (error) {
      return null;
    }
  };

  const normalizeRecentProduct = (value) => {
    if (!value || !['string', 'number'].includes(typeof value.id)) return null;
    const url = safeProductURL(value.url);
    if (!url || typeof value.title !== 'string' || !value.title.trim()) return null;
    return {
      id: String(value.id),
      url,
      title: value.title.trim().slice(0, 160),
      type: typeof value.type === 'string' ? value.type.trim().slice(0, 120) : '',
      image: safeImageURL(value.image),
      imageAlt: typeof value.imageAlt === 'string' ? value.imageAlt.trim().slice(0, 240) : value.title.trim(),
      viewedAt: Number(value.viewedAt) || Date.now()
    };
  };

  const readRecentProducts = () => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
      return Array.isArray(stored) ? stored.map(normalizeRecentProduct).filter(Boolean) : [];
    } catch (error) {
      return [];
    }
  };

  const writeRecentProducts = (products) => {
    try {
      window.localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(products.slice(0, 8)));
    } catch (error) {
      // Storage can be unavailable in private or restricted browser contexts.
    }
  };

  const initRecentlyViewed = (section) => {
    if (section.dataset.recentlyViewedReady === 'true') return;
    const current = normalizeRecentProduct(parseJSON(section.querySelector('[data-current-product]')));
    const list = section.querySelector('[data-recently-viewed-list]');
    const template = section.querySelector('[data-recently-viewed-template]');
    if (!current || !list || !template) return;

    section.dataset.recentlyViewedReady = 'true';
    const stored = readRecentProducts();
    const previous = stored.filter((item) => item.id !== current.id).slice(0, 4);

    previous.forEach((item) => {
      const fragment = template.content.cloneNode(true);
      const link = fragment.querySelector('[data-recently-viewed-link]');
      const image = fragment.querySelector('[data-recently-viewed-image]');
      link.href = item.url;
      setText(fragment.querySelector('[data-recently-viewed-title]'), item.title);
      setText(fragment.querySelector('[data-recently-viewed-type]'), item.type);

      if (item.image) {
        image.src = item.image;
        image.alt = item.imageAlt;
      } else {
        image.remove();
      }
      list.append(fragment);
    });

    section.hidden = previous.length === 0;
    current.viewedAt = Date.now();
    writeRecentProducts([current, ...stored.filter((item) => item.id !== current.id)]);
  };

  const updateSlideshow = (slideshow) => {
    const track = slideshow.querySelector('[data-baring-slideshow-track]');
    const slides = [...slideshow.querySelectorAll('[data-baring-slide]')];
    if (!track || !slides.length) return;
    const current = slides.reduce((closest, slide, index) => {
      const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
      return distance < closest.distance ? { index, distance } : closest;
    }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
    const previous = slideshow.querySelector('[data-baring-slide-control="previous"]');
    const next = slideshow.querySelector('[data-baring-slide-control="next"]');
    if (previous) previous.disabled = current === 0;
    if (next) next.disabled = current === slides.length - 1;
    setText(slideshow.querySelector('[data-baring-slide-status]'), `Slide ${current + 1} of ${slides.length}`);
    slideshow.dataset.currentSlide = String(current);
  };

  const initSlideshow = (slideshow) => {
    if (slideshow.dataset.slideshowReady === 'true') return;
    slideshow.dataset.slideshowReady = 'true';
    const track = slideshow.querySelector('[data-baring-slideshow-track]');
    if (!track) return;
    let frame = 0;
    track.addEventListener('scroll', () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateSlideshow(slideshow);
      });
    }, { passive: true });
    updateSlideshow(slideshow);
  };

  const initBeforeAfter = (section) => {
    if (section.dataset.beforeAfterReady === 'true') return;
    section.dataset.beforeAfterReady = 'true';
    const frame = section.querySelector('.product-before-after__frame');
    const range = section.querySelector('[data-baring-before-after-range]');
    if (!frame || !range) return;

    const measure = () => frame.style.setProperty('--comparison-height', `${frame.getBoundingClientRect().height}px`);
    const render = () => frame.style.setProperty('--comparison-position', `${range.value}%`);
    range.addEventListener('input', render);
    render();
    measure();
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(frame);
  };

  const initHotspots = (scope) => {
    scope.querySelectorAll('[data-baring-hotspot]').forEach((hotspot) => {
      if (hotspot.dataset.hotspotReady === 'true') return;
      hotspot.dataset.hotspotReady = 'true';
      hotspot.addEventListener('toggle', () => {
        if (!hotspot.open) return;
        const stage = hotspot.closest('.product-hotspots__stage');
        stage?.querySelectorAll('[data-baring-hotspot][open]').forEach((peer) => {
          if (peer !== hotspot) peer.removeAttribute('open');
        });
      });
    });
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-baring-product]').forEach(initVariantPicker);
    scope.querySelectorAll('[data-baring-recently-viewed]').forEach(initRecentlyViewed);
    scope.querySelectorAll('[data-baring-slideshow]').forEach(initSlideshow);
    scope.querySelectorAll('[data-baring-before-after]').forEach(initBeforeAfter);
    initHotspots(scope);
  };

  document.addEventListener('click', (event) => {
    const zoomTrigger = event.target.closest('[data-baring-zoom-open]');
    if (zoomTrigger) {
      const dialog = document.querySelector('[data-baring-zoom-dialog]');
      const image = dialog?.querySelector('[data-baring-zoom-image]');
      if (!dialog || !image) return;
      zoomOpener = zoomTrigger;
      image.src = zoomTrigger.dataset.zoomSrc || '';
      image.alt = zoomTrigger.dataset.zoomAlt || '';
      document.documentElement.classList.add('baring-zoom-open');
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      dialog.querySelector('[data-baring-zoom-close]')?.focus();
      return;
    }

    const zoomClose = event.target.closest('[data-baring-zoom-close]');
    if (zoomClose) {
      const dialog = zoomClose.closest('[data-baring-zoom-dialog]');
      if (typeof dialog?.close === 'function') dialog.close();
      else dialog?.removeAttribute('open');
      return;
    }

    const slideControl = event.target.closest('[data-baring-slide-control]');
    if (slideControl) {
      const slideshow = slideControl.closest('[data-baring-slideshow]');
      const track = slideshow?.querySelector('[data-baring-slideshow-track]');
      const slides = [...(slideshow?.querySelectorAll('[data-baring-slide]') || [])];
      if (!slideshow || !track || !slides.length) return;
      const current = Number(slideshow.dataset.currentSlide || 0);
      const direction = slideControl.dataset.baringSlideControl === 'previous' ? -1 : 1;
      const targetIndex = Math.max(0, Math.min(slides.length - 1, current + direction));
      track.scrollTo({
        left: slides[targetIndex].offsetLeft,
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
    }
  });

  document.addEventListener('click', (event) => {
    const dialog = event.target.matches('[data-baring-zoom-dialog]') ? event.target : null;
    if (dialog && event.target === dialog) dialog.close();
  });

  document.addEventListener('close', (event) => {
    if (!event.target.matches?.('[data-baring-zoom-dialog]')) return;
    document.documentElement.classList.remove('baring-zoom-open');
    zoomOpener?.focus();
    zoomOpener = null;
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const hotspot = document.querySelector('[data-baring-hotspot][open]');
    if (hotspot) {
      hotspot.removeAttribute('open');
      hotspot.querySelector('summary')?.focus();
    }
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.('[data-baring-slide]');
    const track = slide?.closest('[data-baring-slideshow-track]');
    if (slide && track) track.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
